import { UserDetails } from '@statsbomb/parachute-types';

type ValidEntityUserResponse = { valid: true; error: null; userDetails: UserDetails };
type InvalidEntityUserResponse = { valid: false; error: string; userDetails: UserDetails | null };

const entityIsSuspended = (entity: UserDetails['entity']) => entity.suspended;

const entityHasNotStarted = (entity: UserDetails['entity']) =>
  entity.start_date && +new Date(entity.start_date) > Date.now();

const entityHasExpired = (entity: UserDetails['entity']) => entity.end_date && +new Date(entity.end_date) < Date.now();

const featuresHasNoData = (features: UserDetails['features']) => features.licensed_game_count === 0;

const userHasNoToolAccess = (user: UserDetails['user']) => !user.has_tool_access;

export const isValidEntityUser = (
  userDetails: UserDetails | null,
): ValidEntityUserResponse | InvalidEntityUserResponse => {
  if (!userDetails) return { valid: false, error: 'no_tool_access', userDetails: null };

  const { entity, features, user } = userDetails;

  if (userHasNoToolAccess(user)) return { valid: false, error: 'no_tool_access', userDetails };
  if (entityHasNotStarted(entity)) return { valid: false, error: 'not_started', userDetails };
  if (entityHasExpired(entity)) return { valid: false, error: 'expired', userDetails };
  if (entityIsSuspended(entity)) return { valid: false, error: 'suspended', userDetails };

  // this needs to be last, as if any of the other errors are true, this one will also be true
  if (featuresHasNoData(features)) return { valid: false, error: 'no_data', userDetails };

  return { valid: true, error: null, userDetails };
};

const automationEntities: ReadonlyArray<string> = ['Hudl HSB - Test Automation'];
const entitiesThatSeeDevTeamFeatures: ReadonlyArray<string> = ['Hudl - Dev Team', ...automationEntities];
const entitiesThatSeeAlphaTestingFeatures: ReadonlyArray<string> = [
  'Hudl - Alpha Testing',
  ...entitiesThatSeeDevTeamFeatures,
];

/**
 * Use this to gate features that are only visible to the internal dev team.
 * This is the most restrictive tier — use it for work-in-progress or experimental
 * features that aren't ready for broader testing yet.
 */
export const canUserSeeDevTeamFeatures = (userDetails: UserDetails | null) => {
  if (!userDetails) return false;
  return entitiesThatSeeDevTeamFeatures.includes(userDetails.entity.name);
};

/**
 * Use this to gate features that are ready for broader alpha testing.
 * Alpha testing entities include all dev team entities, so anything behind this
 * flag is also visible internally.
 */
export const canUserSeeAlphaTestingFeatures = (userDetails: UserDetails | null) => {
  if (!userDetails) return false;
  return entitiesThatSeeAlphaTestingFeatures.includes(userDetails.entity.name);
};

export const isAutomationUser = (userDetails: UserDetails | null) => {
  if (!userDetails) return false;
  return automationEntities.includes(userDetails.entity.name);
};
